// 🔐 accounts (09-30): what this browser already holds, so signing in can
// attach it to the account (the backend checks every secret; a wrong or
// stale one is simply refused). Pure: takes a Storage, touches nothing else.
//
//   seats  = every `player_secret_<seatId>` key (the seat id is the suffix)
//   agents = the `spaces_agent_api_keys` map ({ agentId: 'ag_…' }, AgentDashboardPage)
//
// Seats this browser PULLED from the account (your seat following you here) are
// remembered under PULLED_KEY, so signing out removes exactly those and never a
// seat the browser had before.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const AGENT_KEY_RE = /^ag_[0-9a-f]{40}$/;
export const PULLED_KEY = 'myspaice_account_seats_v1';
const SECRET_PREFIX = 'player_secret_';
const MAX_SEATS = 200;
const MAX_AGENTS = 20;
const MAX_PULLED = 500;

export function collectLocalSeats(store, max = MAX_SEATS) {
  const out = [];
  if (!store) return out;
  for (let i = 0; i < store.length && out.length < max; i += 1) {
    const key = store.key(i);
    if (!key || !key.startsWith(SECRET_PREFIX)) continue;
    const id = key.slice(SECRET_PREFIX.length);
    const secret = store.getItem(key);
    if (UUID_RE.test(id) && typeof secret === 'string' && secret.length > 0 && secret.length <= 128) {
      out.push({ player_id: id, player_secret: secret });
    }
  }
  return out;
}

export function collectLocalAgents(store, max = MAX_AGENTS) {
  try {
    const map = JSON.parse((store && store.getItem('spaces_agent_api_keys')) || '{}');
    if (!map || typeof map !== 'object' || Array.isArray(map)) return [];
    return [...new Set(Object.values(map).filter((k) => typeof k === 'string' && AGENT_KEY_RE.test(k)))]
      .slice(0, max)
      .map((api_key) => ({ api_key }));
  } catch (e) { return []; }
}

function readPulled(store) {
  try {
    const list = JSON.parse((store && store.getItem(PULLED_KEY)) || '[]');
    return Array.isArray(list) ? list.filter((p) => p && UUID_RE.test(p.seat) && UUID_RE.test(p.room)) : [];
  } catch (e) { return []; }
}

/**
 * Store a seat the account handed this device, and remember what it replaced.
 * A seat whose secret this browser already held is NOT recorded as pulled (it
 * was here before, so sign-out keeps it); the room's previous seat is kept as
 * `prev` so sign-out can put it back.
 */
export function adoptPulledSeat(store, { seat, room, secret }) {
  if (!store || !UUID_RE.test(seat) || !UUID_RE.test(room) || typeof secret !== 'string' || !secret) return false;
  const hadIt = store.getItem(`${SECRET_PREFIX}${seat}`) === secret;
  const before = store.getItem(`humanPlayerId_${room}`);
  store.setItem(`${SECRET_PREFIX}${seat}`, secret);
  store.setItem(`humanPlayerId_${room}`, seat);
  const list = readPulled(store);
  const known = list.find((p) => p.seat === seat);
  if (!hadIt && !known) {
    list.push({ seat, room, ...(before && before !== seat && UUID_RE.test(before) ? { prev: before } : {}) });
    store.setItem(PULLED_KEY, JSON.stringify(list.slice(-MAX_PULLED)));
  }
  return true;
}

/**
 * A seat this device MADE while signed in, now held by the account: it leaves
 * the device at sign-out like a pulled one (a friend's laptop keeps nothing of
 * yours) and comes back with the account. No `prev`: the room had no seat here.
 */
export function rememberAccountSeat(store, { seat, room }) {
  if (!store || !UUID_RE.test(seat) || !UUID_RE.test(room)) return false;
  const list = readPulled(store);
  if (!list.some((p) => p.seat === seat)) {
    list.push({ seat, room });
    store.setItem(PULLED_KEY, JSON.stringify(list.slice(-MAX_PULLED)));
  }
  return true;
}

/**
 * Sign-out: forget the seats this device pulled from the account, and nothing
 * else. A room this device had its own seat in goes back to that seat. Newest
 * first, so a chain (own L → pulled A → pulled B) unwinds back to L.
 */
export function forgetPulledSeats(store) {
  if (!store) return 0;
  const list = readPulled(store);
  for (const { seat, room, prev } of [...list].reverse()) {
    store.removeItem(`${SECRET_PREFIX}${seat}`);
    if (store.getItem(`humanPlayerId_${room}`) !== seat) continue;
    if (prev && UUID_RE.test(prev) && store.getItem(`${SECRET_PREFIX}${prev}`)) store.setItem(`humanPlayerId_${room}`, prev);
    else store.removeItem(`humanPlayerId_${room}`);
  }
  store.removeItem(PULLED_KEY);
  return list.length;
}
